import { supabase } from './supabase.js'
import { todayISO } from './format.js'

/**
 * Net worth. A profile for every place money sits, with a balance he types.
 *
 * The rule that shapes this file: **nothing moves a balance that he did not
 * do himself.** The app never infers a balance, never estimates one, and
 * never posts into an account off the back of something it worked out.
 *
 * There is exactly one way money arrives here other than him typing it: a
 * push to savings, which is him saying "I moved this amount to that account"
 * and ticking it. That is still his action — it just starts on the Cashflow
 * screen instead of this one. Every such deposit writes an adjustment row
 * naming the payable it came from, so the account's history says plainly
 * where each peso came from and unticking reverses that exact row.
 *
 * Type decides which side of the total a balance lands on, so he never types
 * a minus sign.
 */

export const TYPES = [
  { key: 'savings',     label: 'Bank or savings', sign: +1 },
  { key: 'wallet',      label: 'E-wallet',        sign: +1 },
  { key: 'investment',  label: 'Investment',      sign: +1 },
  { key: 'credit_card', label: 'Credit card',     sign: -1 },
  { key: 'loan',        label: 'Loan',            sign: -1 },
]

const SIGN = Object.fromEntries(TYPES.map((t) => [t.key, t.sign]))
export const signOf = (type) => SIGN[type] ?? 1
export const isDebt = (type) => signOf(type) < 0

export function totalOf(accounts) {
  return accounts
    .filter((a) => a.include_in_net_worth && !a.archived_at)
    .reduce((s, a) => s + signOf(a.type) * Number(a.balance || 0), 0)
}

async function logIt(kind, summary, targetId) {
  try {
    await supabase.from('activity_log').insert({
      kind, summary, target_type: 'account', target_id: targetId ?? null,
    })
  } catch {
    /* never fail the real write */
  }
}

export async function getAccounts(includeArchived = false) {
  let q = supabase.from('accounts').select('*').order('sort_order').order('created_at')
  if (!includeArchived) q = q.is('archived_at', null)
  const { data, error } = await q
  if (error) throw error
  return data ?? []
}

export async function getSnapshots(limit = 12) {
  const { data, error } = await supabase
    .from('net_worth_snapshots')
    .select('*')
    .order('taken_on', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data ?? []).reverse()
}

export async function addAccount(a) {
  const { data, error } = await supabase
    .from('accounts')
    .insert({
      name: a.name.trim(),
      institution: a.institution?.trim() || null,
      type: a.type,
      balance: a.balance,
      include_in_net_worth: a.include !== false,
    })
    .select()
    .single()
  if (error) throw error
  await logIt('account.add', a.name.trim() + ' added', data.id)
  return data
}

/**
 * Every balance change writes an adjustment row, and that row only ever says
 * he changed it. There is no other way a balance moves.
 */
export async function setBalance(account, newBalance, reason) {
  const old = Number(account.balance || 0)
  if (old === newBalance && !reason) return
  const why = reason?.trim()
  if (!why) throw new Error('Explain this balance correction.')

  const { error } = await supabase
    .from('accounts')
    .update({ balance: newBalance })
    .eq('id', account.id)
  if (error) throw error

  const { error: e2 } = await supabase.from('account_adjustments').insert({
    account_id: account.id,
    changed_on: todayISO(),
    old_balance: old,
    new_balance: newBalance,
    kind: 'correction',
    reason: why,
  })
  if (e2) throw e2

  await logIt('account.balance', account.name + ' updated', account.id)
}

/*
 * Money in, money out.
 *
 * Typing a new balance is how this started and it was the wrong primitive.
 * "It was 350,000, now it's 355,000" throws away the only thing worth
 * knowing, which is that 5,000 went in. A balance you edit has no history;
 * a balance you move has nothing but history.
 *
 * So a move is the normal way a balance changes. The balance is still the
 * stored truth — it is not a running sum of movements, because a sum that
 * drifts from the bank app is worse than a number he can correct — and the
 * correction path stays, one level down, recorded like everything else.
 */

/** What in and out are called depends on which side of the ledger this sits. */
export function moveLabels(type) {
  return isDebt(type)
    ? { up: 'Charge', down: 'Payment', upHint: 'Adds to what you owe', downHint: 'Reduces what you owe' }
    : { up: 'Deposit', down: 'Withdraw', upHint: 'Money in', downHint: 'Money out' }
}

/**
 * Move an amount into or out of an account.
 *
 * `direction` is 'up' or 'down' against the stored balance, so it means the
 * same thing for a savings account and a credit card; only the words change.
 * Returns the new balance so the caller can show it without re-reading.
 */
export async function moveBalance(account, direction, amount, reason) {
  const amt = Math.abs(Number(amount) || 0)
  if (!amt) throw new Error('Enter an amount.')
  const why = reason?.trim()
  if (!why) throw new Error('Enter what this money is for.')

  const old = Number(account.balance || 0)
  const next = direction === 'down' ? old - amt : old + amt

  const { error } = await supabase
    .from('accounts')
    .update({ balance: next })
    .eq('id', account.id)
  if (error) throw error

  const kind = direction === 'down'
    ? (isDebt(account.type) ? 'payment' : 'withdraw')
    : (isDebt(account.type) ? 'charge' : 'deposit')

  /* The row comes back so the caller can point it at what the money was for.
     A movement that funds a trip is only useful if the trip can find it. */
  const { data: adjustment, error: e2 } = await supabase
    .from('account_adjustments')
    .insert({
      account_id: account.id,
      changed_on: todayISO(),
      old_balance: old,
      new_balance: next,
      kind,
      reason: why,
    })
    .select()
    .single()
  if (e2) throw e2

  await logIt('account.' + kind, account.name + ' ' + kind + ' ' + amt, account.id)
  return { balance: next, adjustment }
}

/** Accounts a push can land in. Paying down a card or a loan is an expense
    he already tracks as an MOB payable, so those are deliberately not here. */
export const DESTINATION_TYPES = ['savings', 'wallet', 'investment']
export const canReceive = (account) =>
  DESTINATION_TYPES.includes(account.type) && !account.archived_at

/**
 * Posts a push to savings. Returns the adjustment so the caller can say what
 * happened; throws if the account has gone, because silently dropping the
 * deposit would leave the payable claiming money that never arrived.
 */
export async function depositFromPayable(payable, note) {
  const { data: account, error } = await supabase
    .from('accounts')
    .select('*')
    .eq('id', payable.to_account_id)
    .single()
  if (error) throw error
  if (!account) throw new Error('That account no longer exists.')

  const amount = Number(payable.amount) || 0
  const old = Number(account.balance || 0)
  const next = old + amount

  const { error: e1 } = await supabase
    .from('accounts')
    .update({ balance: next })
    .eq('id', account.id)
  if (e1) throw e1

  const { error: e2 } = await supabase.from('account_adjustments').insert({
    account_id: account.id,
    changed_on: todayISO(),
    old_balance: old,
    new_balance: next,
    /* A push from Cashflow is its own kind. It is money in, but he did not
       type it, and the history should say which. */
    kind: 'push',
    reason: note || payable.name,
    source_payable_id: payable.id,
  })
  if (e2) throw e2

  await logIt('account.deposit', amount + ' pushed to ' + account.name, account.id)
  return { account, amount, old, next }
}

/**
 * Takes it back out. Works from the adjustment row rather than the payable's
 * current amount, so editing the amount after ticking can never leave a
 * balance that reverses to the wrong number.
 */
export async function reverseDeposit(payableId) {
  const { data: rows, error } = await supabase
    .from('account_adjustments')
    .select('*')
    .eq('source_payable_id', payableId)
  if (error) throw error
  if (!rows?.length) return null

  let touched = null
  for (const row of rows) {
    const moved = Number(row.new_balance || 0) - Number(row.old_balance || 0)
    const { data: account, error: e1 } = await supabase
      .from('accounts')
      .select('*')
      .eq('id', row.account_id)
      .single()
    if (e1) throw e1

    if (account) {
      const { error: e2 } = await supabase
        .from('accounts')
        .update({ balance: Number(account.balance || 0) - moved })
        .eq('id', account.id)
      if (e2) throw e2
      touched = account
    }

    const { error: e3 } = await supabase
      .from('account_adjustments')
      .delete()
      .eq('id', row.id)
    if (e3) throw e3
  }

  if (touched) await logIt('account.deposit.undo', touched.name + ' deposit reversed', touched.id)
  return touched
}

/** What a push has put into this account since he last set the balance by hand. */
export function pushedSince(adjustments) {
  const rows = [...adjustments].sort((a, b) =>
    String(b.changed_on).localeCompare(String(a.changed_on)),
  )
  let total = 0
  for (const r of rows) {
    if (!r.source_payable_id) break /* a manual set — everything before it is settled */
    total += Number(r.new_balance || 0) - Number(r.old_balance || 0)
  }
  return total
}

export async function updateAccount(id, patch) {
  const { error } = await supabase.from('accounts').update(patch).eq('id', id)
  if (error) throw error
}

/** Archive, never delete. A closed account keeps its history and leaves the total. */
export async function archiveAccount(account) {
  const { error } = await supabase
    .from('accounts')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', account.id)
  if (error) throw error
  await logIt('account.archive', account.name + ' archived', account.id)
}

export async function unarchiveAccount(account) {
  const { error } = await supabase
    .from('accounts')
    .update({ archived_at: null })
    .eq('id', account.id)
  if (error) throw error
}

export async function getAdjustments(accountId, limit = 20) {
  const { data, error } = await supabase
    .from('account_adjustments')
    .select('*')
    .eq('account_id', accountId)
    .order('changed_on', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data ?? []
}

/**
 * One snapshot per day, so the line has points without him doing anything.
 * Upserted rather than inserted — editing two accounts on the same day should
 * correct today's point, not add a second one.
 */
export async function snapshot(accounts) {
  const live = accounts.filter((a) => a.include_in_net_worth && !a.archived_at)
  const breakdown = Object.fromEntries(
    live.map((a) => [a.id, { name: a.name, type: a.type, balance: Number(a.balance || 0) }]),
  )
  const { error } = await supabase
    .from('net_worth_snapshots')
    .upsert(
      { taken_on: todayISO(), total: totalOf(accounts), breakdown },
      { onConflict: 'user_id,taken_on' },
    )
  if (error) throw error
}
